import React, { createContext, useContext, useMemo } from 'react';
import { Globe } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { translate } from './translations';
import { languages, splitLanguagePath, languagePath } from './locale';
export { languages, splitLanguagePath, languagePath } from './locale';
const defaultValue = { language: 'es', t: text => text, href: path => path };
const LocaleContext = createContext(defaultValue);
export function LocaleProvider({ children }) {
  const { pathname } = useLocation();
  const language = splitLanguagePath(pathname).language;
  const value = useMemo(() => ({ language, t: text => translate(text, language), href: path => languagePath(path, language) }), [language]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}
export const useLocale = () => useContext(LocaleContext);

// Localize presentation only: form values, IDs, events and user input stay intact.
export function localizeTree(node, t, href) {
  if (typeof node === 'string') return t(node);
  if (Array.isArray(node)) return React.Children.map(node, child => localizeTree(child, t, href));
  if (!React.isValidElement(node) || node.props['data-no-translate']) return node;
  const props = {};
  for (const key of ['aria-label', 'aria-roledescription', 'placeholder', 'title', 'alt']) if (typeof node.props[key] === 'string') props[key] = t(node.props[key]);
  for (const key of ['href', 'to']) if (typeof node.props[key] === 'string') props[key] = href(node.props[key]);
  if (node.type === 'option' && node.props.value === undefined && typeof node.props.children === 'string') props.value = node.props.children;
  // Do not translate editable fields or text typed by a visitor.
  if (!['input','textarea'].includes(node.type)) props.children = localizeTree(node.props.children, t, href);
  return React.cloneElement(node, props);
}
export function Localized({ children }) {
  const { t, href } = useLocale();
  return localizeTree(children, t, href);
}
export function LanguageSelector() {
  const { language, t } = useLocale();
  const location = useLocation();
  const navigate = useNavigate();
  return <label className="bz-language-select" data-no-translate="true" title={t('Cambiar idioma')}><Globe className="bz-language-globe" size={20} strokeWidth={1.6} aria-hidden="true"/><span className="bz-visually-hidden">{t('Idioma')}</span><select aria-label={t('Idioma')} value={language} onChange={event => navigate(languagePath(splitLanguagePath(location.pathname).path, event.target.value) + location.search + location.hash)}>{languages.map(item => <option key={item.code} value={item.code}>{item.name}</option>)}</select></label>;
}
export function useLocalizedNavigate() {
  const navigate = useNavigate();
  const { href } = useLocale();
  return (path, options) => navigate(href(path), options);
}
